import React, { useState, useEffect, useCallback } from 'react';

const PROPERTY_API_URL = 'https://property-lookup-api.anassk.workers.dev';
const PROPERTY_API_KEY = '19285196e984c67c9c96784becbd3464e692815a4393e4c948be460a2019182e';
const GMAPS_KEY = 'AIzaSyAvqhg9a9UGp1QiJ6kF5j5RqMCaFzHj7CY';

// Maps preference helpers
function getMapsPreference() { return localStorage.getItem('atr_maps_pref') || 'google'; }
function setMapsPreferenceStorage(pref) { localStorage.setItem('atr_maps_pref', pref); }

function directionsUrl(destAddr, destLat, destLng, pref) {
  if (pref === 'apple') {
    return destLat && destLng
      ? 'https://maps.apple.com/?daddr=' + destLat + ',' + destLng + '&dirflg=d'
      : 'https://maps.apple.com/?daddr=' + encodeURIComponent(destAddr) + '&dirflg=d';
  }
  return 'https://www.google.com/maps/dir/?api=1&destination=' + encodeURIComponent(destAddr);
}

function buildRouteUrl(leads, pref) {
  const addrs = leads.map(l => l.fullAddress || l.address);
  if (pref === 'apple') {
    const dest = addrs.map(a => encodeURIComponent(a)).join('+to:');
    return 'https://maps.apple.com/?dirflg=d&daddr=' + dest;
  }
  const encoded = addrs.map(a => encodeURIComponent(a));
  if (encoded.length >= 2) return 'https://www.google.com/maps/dir/' + encoded.join('/');
  if (encoded.length === 1) return 'https://www.google.com/maps/search/?api=1&query=' + encoded[0];
  return '';
}

// Haversine distance in miles
function distMi(lat1, lng1, lat2, lng2) {
  if (!lat1 || !lng1 || !lat2 || !lng2) return Infinity;
  const R = 3959;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180) * Math.cos(lat2*Math.PI/180) * Math.sin(dLng/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}

export default function DailyPlanPanel({ onNavigateToAddress, onDriveTo }) {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [expandedLead, setExpandedLead] = useState(null);
  const [mapsPref, setMapsPref] = useState(getMapsPreference);
  const [stormFilter, setStormFilter] = useState(null); // county name or null

  const fetchPlan = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${PROPERTY_API_URL}/api/weekly-plan`, {
        headers: { 'X-API-Key': PROPERTY_API_KEY }
      });
      const data = await res.json();
      if (data.success && data.plan) {
        setPlan(data);
        // Auto-select today's day
        const todayName = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'][new Date().getDay()];
        const todayDay = data.plan.days?.find(d => d.day === todayName);
        setSelectedDay(todayDay ? todayName : data.plan.days?.[0]?.day);
      } else {
        setError(data.error || 'No plan data');
      }
    } catch (e) {
      setError('Failed to load plan');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPlan(); }, [fetchPlan]);

  if (loading) return <div className="daily-plan-panel"><div className="dp-loading">Loading plan...</div></div>;
  if (error) return <div className="daily-plan-panel"><div className="dp-error">{error}</div></div>;
  if (!plan?.plan) return <div className="daily-plan-panel"><div className="dp-empty">No plan available yet.</div></div>;

  const days = plan.plan.days || [];
  const currentDay = days.find(d => d.day === selectedDay) || days[0];
  const allLeads = currentDay?.leads || [];

  // Filter by storm zone if active
  const leads = stormFilter ? allLeads.filter(l => {
    if (!l.stormHit) return false;
    if (l.stormCounty === stormFilter) return true;
    // Proximity fallback: within 15mi of storm zone center
    const zone = plan.stormZones?.find(z => z.county === stormFilter);
    if (zone && l.lat && l.lng) return distMi(l.lat, l.lng, zone.latitude, zone.longitude) < 15;
    return false;
  }) : allLeads;

  return (
    <div className="daily-plan-panel">
      {/* Storm Alert */}
      {plan.freshStorms?.storms?.length > 0 && (
        <div className="dp-storm-alert">
          <div className="dp-storm-icon">NEW STORM</div>
          <div className="dp-storm-text">
            <strong>{plan.freshStorms.storms[0].size}" hail — {plan.freshStorms.storms[0].county} County</strong>
            <span>near {plan.freshStorms.storms[0].location} — go NOW</span>
          </div>
        </div>
      )}

      {/* Storm Zones — clickable filter */}
      {plan.stormZones?.length > 0 && (
        <div className="dp-storm-zones">
          <div className="dp-sz-label">Storm Zones ({plan.stormZones.length})</div>
          <div className="dp-sz-list">
            {stormFilter && (
              <span className="dp-sz-tag dp-sz-active" onClick={() => setStormFilter(null)} style={{background:'#3a1a1a',borderColor:'#d62828',color:'#eb5757',cursor:'pointer'}}>
                Clear Filter &times;
              </span>
            )}
            {[...new Set(plan.stormZones.map(s => s.county))].slice(0, 6).map(county => {
              const isActive = stormFilter === county;
              const stormLeadCount = days.flatMap(d => d.leads || []).filter(l => l.stormHit && l.stormCounty === county).length;
              // Also count by proximity if stormCounty not set
              const zone = plan.stormZones.find(z => z.county === county);
              const nearbyCount = stormLeadCount || days.flatMap(d => d.leads || []).filter(l => {
                if (!l.stormHit || !l.lat || !l.lng || !zone) return false;
                return distMi(l.lat, l.lng, zone.latitude, zone.longitude) < 15;
              }).length;
              return (
                <span
                  key={county}
                  className={`dp-sz-tag ${isActive ? 'dp-sz-active' : ''}`}
                  onClick={() => setStormFilter(isActive ? null : county)}
                  style={isActive ? {background:'#e85d04',color:'#fff',cursor:'pointer'} : {cursor:'pointer'}}
                >
                  {county} Co. {nearbyCount > 0 && `(${nearbyCount})`}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Maps Preference Toggle */}
      <div className="dp-maps-toggle">
        <button className={mapsPref === 'google' ? 'active' : ''} onClick={() => { setMapsPreferenceStorage('google'); setMapsPref('google'); }}>Google Maps</button>
        <button className={mapsPref === 'apple' ? 'active' : ''} onClick={() => { setMapsPreferenceStorage('apple'); setMapsPref('apple'); }}>Apple Maps</button>
      </div>

      {/* Day Selector */}
      <div className="dp-day-nav">
        {days.map(d => (
          <button
            key={d.day}
            className={`dp-day-btn ${selectedDay === d.day ? 'active' : ''}`}
            onClick={() => setSelectedDay(d.day)}
          >
            <span className="dp-day-abbr">{d.day.slice(0, 3)}</span>
            <span className="dp-day-count">{d.leads.length}</span>
          </button>
        ))}
      </div>

      {/* Day Header */}
      <div className="dp-day-header">
        <div>
          <h3>{currentDay.day}</h3>
          <span className="dp-day-area">{currentDay.area}</span>
        </div>
        <div className="dp-day-meta">
          <span>{leads.length} leads</span>
          {currentDay.driveTimeEstimate && (
            <span className="dp-drive-time">{currentDay.driveTimeEstimate}</span>
          )}
        </div>
      </div>

      {/* Storm filter info */}
      {stormFilter && (
        <div className="dp-storm-filter-info">
          Showing {leads.length} storm-hit leads in <strong>{stormFilter} County</strong>
          {leads.length === 0 && <span> — try another day or zone</span>}
        </div>
      )}

      {/* Full Day Route Button */}
      {leads.length >= 2 && (
        <a
          className="dp-route-btn"
          href={buildRouteUrl(leads, mapsPref)}
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="dp-route-icon">&#x1F697;</span>
          <span className="dp-route-text">
            <strong>Start Driving — {leads.length} stops</strong>
            <span>{stormFilter ? `${stormFilter} Co. storm zone` : currentDay.driveTimeEstimate || ''}</span>
          </span>
          <span className="dp-route-arrow">&#x2192;</span>
        </a>
      )}

      {/* Lead Cards */}
      <div className="dp-leads">
        {leads.map((lead, i) => (
          <div
            key={lead.fullAddress || i}
            className={`dp-lead-card ${expandedLead === i ? 'expanded' : ''} ${lead.permitVerified ? 'verified' : ''}`}
            onClick={e => {
              // Don't collapse card when tapping links/buttons inside
              let t = e.target;
              while (t && t !== e.currentTarget) {
                if (t.tagName === 'A' || t.tagName === 'BUTTON') return;
                t = t.parentElement;
              }
              setExpandedLead(expandedLead === i ? null : i);
            }}
          >
            <div className="dp-lead-header">
              <div className="dp-lead-rank">{i + 1}</div>
              <div className="dp-lead-info">
                <div className="dp-lead-address">{lead.address}</div>
                <div className="dp-lead-owner">Ask for <strong>{(lead.owner || 'Owner').split(' ')[0]}</strong> · {lead.owner}</div>
              </div>
              <div className="dp-lead-stats">
                <div className={`dp-lead-score ${lead.score >= 60 ? 'high' : lead.score >= 40 ? 'med' : 'low'}`}>
                  {lead.score >= 60 ? 'HOT' : lead.score >= 40 ? 'WARM' : 'GOOD'}
                </div>
                <div className="dp-lead-cost">{lead.roofCostEstimate || '—'}</div>
              </div>
            </div>

            {/* Expanded Details */}
            {expandedLead === i && (
              <div className="dp-lead-details">
                {/* Street View Image — uses lat/lng for better coverage, falls back to satellite */}
                <img
                  className="dp-streetview-img"
                  src={lead.lat && lead.lng
                    ? `https://maps.googleapis.com/maps/api/streetview?size=600x300&return_error_code=true&location=${lead.lat},${lead.lng}&source=outdoor&key=${GMAPS_KEY}`
                    : `https://maps.googleapis.com/maps/api/streetview?size=600x300&return_error_code=true&location=${encodeURIComponent(lead.fullAddress || lead.address)}&key=${GMAPS_KEY}`
                  }
                  alt={`Street view of ${lead.address}`}
                  onClick={e => e.stopPropagation()}
                  onError={e => {
                    if (!e.target.dataset.fallback && lead.lat && lead.lng) {
                      e.target.dataset.fallback = '1';
                      e.target.src = `https://maps.googleapis.com/maps/api/staticmap?center=${lead.lat},${lead.lng}&zoom=19&size=600x300&maptype=satellite&markers=color:red%7C${lead.lat},${lead.lng}&key=${GMAPS_KEY}`;
                    } else {
                      e.target.style.display = 'none';
                    }
                  }}
                />

                {/* Key Facts */}
                <div className="dp-lead-facts">
                  <div className="dp-fact">
                    <span className="dp-fact-label">Home</span>
                    <span className="dp-fact-value">{lead.value ? `$${(lead.value/1000).toFixed(0)}K` : '—'}</span>
                  </div>
                  <div className="dp-fact">
                    <span className="dp-fact-label">Roof</span>
                    <span className="dp-fact-value">{lead.roofAge || lead.buildingAge}yr old</span>
                  </div>
                  <div className="dp-fact">
                    <span className="dp-fact-label">Material</span>
                    <span className="dp-fact-value">{lead.roofType || 'Asphalt'}</span>
                  </div>
                  <div className="dp-fact">
                    <span className="dp-fact-label">Job Value</span>
                    <span className="dp-fact-value" style={{color: '#f2c94c'}}>{lead.roofCostEstimate || '—'}</span>
                  </div>
                </div>

                {/* Storm proximity */}
                {lead.stormHit && (
                  <div className="dp-storm-hit">
                    Storm damage area — insurance will likely cover replacement
                  </div>
                )}

                {/* Why this lead */}
                <div className="dp-lead-reasons">
                  {lead.permitVerified && <span className="dp-reason-tag permit">Original roof confirmed</span>}
                  {(lead.roofAge || lead.buildingAge) >= 25 && <span className="dp-reason-tag">Old roof — needs replacing</span>}
                  {lead.value >= 400000 && <span className="dp-reason-tag">High-value home</span>}
                  {lead.stormHit && <span className="dp-reason-tag">Storm hit nearby</span>}
                </div>

                {/* Action Buttons */}
                <div className="dp-sv-actions" onClick={e => e.stopPropagation()}>
                  {onDriveTo && (
                    <button
                      className="dp-sv-btn dp-sv-btn-drive"
                      onClick={() => onDriveTo({
                        address: lead.fullAddress || lead.address,
                        streetAddress: lead.address,
                        owner: lead.owner,
                        lat: lead.lat, lng: lead.lng,
                        city: lead.city, state: lead.state, zip: lead.zip,
                      })}
                    >
                      🚗 Drive Here
                    </button>
                  )}
                  <a
                    className="dp-sv-btn dp-sv-btn-directions"
                    href={directionsUrl(lead.fullAddress || lead.address, lead.lat, lead.lng, mapsPref)}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {mapsPref === 'apple' ? 'Directions' : 'Directions'}
                  </a>
                  <a
                    className="dp-sv-btn dp-sv-btn-streetview"
                    href={mapsPref === 'apple'
                      ? `https://maps.apple.com/?ll=${lead.lat},${lead.lng}&z=18&t=k`
                      : `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lead.lat},${lead.lng}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Street View
                  </a>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Summary */}
      {plan.summary && (
        <div className="dp-summary">
          <div className="dp-summary-stat">
            <span className="dp-summary-value">{plan.summary.totalAddresses}</span>
            <span className="dp-summary-label">Doors</span>
          </div>
          <div className="dp-summary-stat">
            <span className="dp-summary-value">{days.length}</span>
            <span className="dp-summary-label">Days</span>
          </div>
          <div className="dp-summary-stat">
            <span className="dp-summary-value">${(plan.summary.estimatedRevenue || 0).toLocaleString()}</span>
            <span className="dp-summary-label">Revenue</span>
          </div>
        </div>
      )}

      <div className="dp-updated">
        Week of {plan.weekOf}
        <button className="dp-refresh" onClick={fetchPlan}>Refresh</button>
      </div>
    </div>
  );
}
